import { Client } from "@/Common/Api/Client";
import { EndpointInfoClient } from "./EndpointInfoClient";

interface QueryParams {
    [key: string]: string | string[] | boolean;
}

class ExternalServiceClient {
    private readonly reservedCharactersRegex = /[&=+?#/]/g;

    /**
     * Used to retrieve the access info of an endpoint (token, baseUri, etc.)
     */
    private endpointInfoClient: EndpointInfoClient;

    public constructor(endpointInfoClient: EndpointInfoClient) {
        this.endpointInfoClient = endpointInfoClient;
    }

    public clearCache(): void {
        this.endpointInfoClient.clearCache();
    }

    public get(route: string, body?: any, onSuccess?: (response: any) => void, onError?: (errorMsgs: any) => void): void {
        this.sendRequest(route, "GET", body, onSuccess, onError);
    }

    public getAsync<TResponse>(route: string, pathParams?: string[], queryParams?: QueryParams, allowAbort?: boolean): Promise<TResponse> {
        route = [route, ...(pathParams?.map(p => p.replace(this.reservedCharactersRegex, "")) ?? [])].join("/");
        return this.sendRequestAsync(route, "GET", null, queryParams, allowAbort);
    }

    public post(route: string, body: any, onSuccess?: (response: any) => void, onError?: (errorMsgs: any) => void): void {
        this.sendRequest(route, "POST", body, onSuccess, onError);
    }

    public postAsync<TModel, TResponse>(route: string, body: TModel): Promise<TResponse> {
        return this.sendRequestAsync(route, "POST", body);
    }

    public put(route: string, body: any, onSuccess?: (response: any) => void, onError?: (errorMsgs: any) => void): void {
        this.sendRequest(route, "POST", body, onSuccess, onError);
    }

    public putAsync<TModel, TResponse>(route: string, body: TModel): Promise<TResponse> {
        return this.sendRequestAsync(route, "PUT", body);
    }

    public patchAsync<TModel, TResponse>(route: string, body?: TModel): Promise<TResponse> {
        return this.sendRequestAsync(route, "PATCH", body);
    }

    private privateSendRequest: AbortController | null = null;
    private signal: AbortSignal | null = null;

    public async sendRequestAsync<TModel, TResponse>(route: string, method: string, requestBody?: TModel, queryParams?: QueryParams, allowAbort?: boolean): Promise<TResponse> {
        if (allowAbort && this.privateSendRequest) {
            this.privateSendRequest.abort();
        }
        else if (allowAbort) {
            this.privateSendRequest = new AbortController();
            this.signal = this.privateSendRequest.signal;
        }

        try {
            Client.startLoadingIndicator();
            const accessInfo = await this.endpointInfoClient.getAccessInfo();
            const url = new URL(route, accessInfo.baseUri);
            this.setQueryParams(url, queryParams);

            const response = await fetch(url.toString(), {
                method: method,
                body: requestBody ? JSON.stringify(requestBody) : null,
                mode: "cors",
                headers: new Headers({
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${accessInfo.token}`
                }),
                signal: this.signal
            });

            const data = response.status === 204
                ? null // no content
                : await response.json();

            if (!response.ok)
                throw new Error(`HTTP error! status: ${response.status} ${response.statusText} - ${JSON.stringify(data)}`);

            return data;
        }
        catch (e) {
            console.error(e);
            throw e;
        }
        finally {
            if (allowAbort)
                this.privateSendRequest = null;
            Client.stopLoadingIndicator();
        }
    }

    public async sendRequest(route: string, method: string, body?: any, onSuccess?: (response: any) => void, onError?: (errorMsgs: any) => void): Promise<void> {
        try {
            const accessInfo = await this.endpointInfoClient.getAccessInfo();
            const url = new URL(route, accessInfo.baseUri);
            jQuery.ajax({
                url: url.toString(),
                method: method,
                data: body ? JSON.stringify(body) : null,
                processData: false,
                crossDomain: true,
                jsonp: false,
                contentType: "application/json",
                dataType: "json",
                success: data => {
                    Client.stopLoadingIndicator();
                    onSuccess?.(data);
                },
                error: e => {
                    Client.stopLoadingIndicator();
                    onError?.(e);
                },
                beforeSend: request => {
                    request.setRequestHeader("Authorization", `Bearer ${accessInfo.token}`);
                    Client.startLoadingIndicator();
                }
            });
        }
        catch (e) {
            Client.stopLoadingIndicator();
            onError?.(e);
        }
    }

    private setQueryParams(url: URL, queryParams: QueryParams): void {
        if (queryParams) {
            for (const key of Object.keys(queryParams)) {
                const rawValue = queryParams[key];
                let values: string[] = [];

                if (rawValue) {
                    if (Array.isArray(rawValue)) {
                        values = rawValue.map(val => val.toString());
                    }
                    else {
                        values = [rawValue.toString()];
                    }
                }

                for (const value of values) {
                    url.searchParams.append(key, value.replace(this.reservedCharactersRegex, ""));
                }
            }
        }
    }
}

export { ExternalServiceClient, QueryParams };
